if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.lastAnnouncedMedia = null;
        this.onMediaQueryChange = this.onMediaQueryChange.bind(this);

        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this.mql.addEventListener?.('change', this.onMediaQueryChange);
        this.updateListSemantic();
      }

      connectedCallback() {
        // EventTarget ignores duplicate registrations for the same callback,
        // while this restores the listener if the element is moved/reconnected.
        this.mql?.addEventListener?.('change', this.onMediaQueryChange);
        this.updateListSemantic();
      }

      disconnectedCallback() {
        this.mql?.removeEventListener?.('change', this.onMediaQueryChange);
      }

      onMediaQueryChange() {
        this.updateListSemantic();
      }

      /**
       * Shopify sometimes passes a RAW media id (e.g. "12345"),
       * while our DOM uses a composite id (e.g. "SECTIONID-12345").
       * This normalises both cases to the DOM format.
       */
      resolveMediaId(mediaId) {
        if (!mediaId) return null;
        const id = String(mediaId);

        // Already composite like "1234567890-987654321"
        if (id.includes('-')) return id;

        // Build composite from data-section in <media-gallery>
        const sectionId = this.dataset.section ? String(this.dataset.section) : null;
        if (!sectionId) return id; // fallback: return raw (better than null)

        return `${sectionId}-${id}`;
      }

      onSlideChanged(event) {
        const currentElement = event?.detail?.currentElement;
        const current = currentElement?.dataset?.mediaId;
        if (!current) return;

        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${current}"]`);
        if (thumbnail) this.setActiveThumbnail(thumbnail);

        const position =
          thumbnail?.dataset?.mediaPosition ||
          String(Array.from(this.elements.viewer?.querySelectorAll?.('[data-media-id]') || []).indexOf(currentElement) + 1);
        this.announceLiveRegion(currentElement, position);
      }

      setActiveMedia(mediaId, prepend) {
        const resolvedId = this.resolveMediaId(mediaId);
        if (!resolvedId) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${resolvedId}"]`) ||
          // extra fallback: if someone passed composite-but-wrong, try raw as-is
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnailForPrepend =
              this.elements.thumbnails.querySelector(`[data-target="${resolvedId}"]`) ||
              this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);

            if (activeThumbnailForPrepend) {
              activeThumbnailForPrepend.parentElement.firstChild !== activeThumbnailForPrepend &&
                activeThumbnailForPrepend.parentElement.prepend(activeThumbnailForPrepend);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(`[data-target="${resolvedId}"]`) ||
          this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);

        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion || !activeItem) return;

        const mediaId = activeItem.dataset.mediaId || '';
        if (mediaId && this.lastAnnouncedMedia === mediaId) return;
        if (mediaId) this.lastAnnouncedMedia = mediaId;

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position || '1');
          window.setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };

        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || (image.complete && image.naturalWidth > 0)) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      updateListSemantic() {
        const slider = this.elements.viewer?.slider;
        if (!slider) return;

        const shouldBePresentational = String(this.dataset.desktopLayout || '').includes('thumbnail') && this.mql.matches;
        if (shouldBePresentational) {
          slider.setAttribute('role', 'presentation');
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
          return;
        }

        slider.removeAttribute('role');
        this.elements.viewer.sliderItems.forEach((slide) => slide.removeAttribute('role'));
      }
    }
  );
}
